export type LiquidLevelIntroParagraph = {
	key: string;
	default: string;
};

export type LiquidLevelIntroStep = {
	key: string;
	titleKey: string;
	title: string;
	tabTitleKey: string;
	tabTitle: string;
	subtitleKey: string;
	subtitle: string;
	paragraphs: LiquidLevelIntroParagraph[];
};

/**
 * Educational introduction steps for BLU Liquid Level Sensor ZB (SBSN-001B).
 * Copy follows the product page (short description, use cases, button UI)
 * plus BLE docs for reporting, dual calibration, and sensitivity.
 */
export const getLiquidLevelIntroWizardSteps = (): LiquidLevelIntroStep[] => [
	{
		key: 'device',
		titleKey: 'title_liquid_level_intro_device',
		title: 'Device',
		tabTitleKey: 'title_liquid_level_intro_tab_device',
		tabTitle: 'Device',
		subtitleKey: 'title_liquid_level_intro_device_subtitle',
		subtitle: 'Meet your liquid level sensor',
		paragraphs: [
			{
				key: 'text_liquid_level_intro_device_1',
				default:
					'Monitor liquid levels in non-conductive tanks and containers in real time. Capacitive sensing detects changes in dielectric properties so you can get notifications and automations at the level you choose.',
			},
			{
				key: 'text_liquid_level_intro_device_2',
				default:
					'Use it on plastic tanks and through glass (aquariums or hydroponic reservoirs). Do not place the sensor in the liquid, and do not use it on metal tanks. Suitable for water and other compatible liquids.',
			},
			{
				key: 'text_liquid_level_intro_device_3',
				default:
					'It connects over Bluetooth and Zigbee. In the app it reports Clear (no liquid) or Detected (liquid present).',
			},
		],
	},
	{
		key: 'use_cases',
		titleKey: 'title_liquid_level_intro_use_cases',
		title: 'Use cases',
		tabTitleKey: 'title_liquid_level_intro_tab_use_cases',
		tabTitle: 'Uses',
		subtitleKey: 'title_liquid_level_intro_use_cases_subtitle',
		subtitle: 'Typical places to install it',
		paragraphs: [
			{
				key: 'text_liquid_level_intro_use_cases_1',
				default:
					'Water storage tanks: watch the level in plastic tanks and get notified when they run low or full. Automate pumps or valves to keep the level you want.',
			},
			{
				key: 'text_liquid_level_intro_use_cases_2',
				default:
					'Rainwater collection: track stored rainwater in plastic tanks. Get an alert when you need another source, or start irrigation when enough water has been collected.',
			},
			{
				key: 'text_liquid_level_intro_use_cases_3',
				default:
					'Household containers: track water, detergents, fabric softeners, or other compatible liquids in plastic containers and get a notification before they run out.',
			},
			{
				key: 'text_liquid_level_intro_use_cases_4',
				default:
					'Glass aquariums and hydroponics: monitor the water level through the glass without putting anything in the liquid. Get notified when it drops too low, or automate refilling.',
			},
		],
	},
	{
		key: 'how_it_works',
		titleKey: 'title_liquid_level_intro_working',
		title: 'How it works',
		tabTitleKey: 'title_liquid_level_intro_tab_working',
		tabTitle: 'Works',
		subtitleKey: 'title_liquid_level_intro_working_subtitle',
		subtitle: 'How the sensor reports liquid',
		paragraphs: [
			{
				key: 'text_liquid_level_intro_working_1',
				default:
					'Two sensing electrodes detect whether liquid is present at the mounted height. This is a Clear or Detected reading, not a percentage fill level.',
			},
			{
				key: 'text_liquid_level_intro_working_2',
				default:
					'Over Bluetooth it broadcasts the latest state whenever it changes, and about every 15 seconds otherwise. If Zigbee is enabled, the same state is also sent to bound devices.',
			},
			{
				key: 'text_liquid_level_intro_working_3',
				default:
					'Until at least one calibration point exists, the app shows Not calibrated instead of Clear or Detected.',
			},
		],
	},
	{
		key: 'calibrate',
		titleKey: 'title_liquid_level_intro_calibrate',
		title: 'Calibrate',
		tabTitleKey: 'title_liquid_level_intro_tab_calibrate',
		tabTitle: 'Calibrate',
		subtitleKey: 'title_liquid_level_intro_calibrate_subtitle',
		subtitle: 'Calibrate Empty and Full correctly',
		paragraphs: [
			{
				key: 'text_liquid_level_intro_calibrate_1',
				default:
					'The sensor has independent Empty (Clear) and Full (Detected) calibration. One point is enough to start reporting. When both points are set, thresholds are calculated automatically.',
			},
			{
				key: 'text_liquid_level_intro_calibrate_2',
				default:
					'Press the button twice to start Empty calibration, or three times for Full. The device confirms with a flashing red LED. Do not touch the sensor while it calibrates.',
			},
			{
				key: 'text_liquid_level_intro_calibrate_3',
				default:
					'After you trigger calibration, step away. The LED flashes so you can remove your hands, then continues until the reading is stable and confirms the result.',
			},
			{
				key: 'text_liquid_level_intro_calibrate_4',
				default:
					'To clear calibration data and start over, press and hold the button for 5 seconds.',
			},
		],
	},
	{
		key: 'adjust',
		titleKey: 'title_liquid_level_intro_adjust',
		title: 'Adjust',
		tabTitleKey: 'title_liquid_level_intro_tab_adjust',
		tabTitle: 'Adjust',
		subtitleKey: 'title_liquid_level_intro_adjust_subtitle',
		subtitle: 'Sensitivity and LED feedback',
		paragraphs: [
			{
				key: 'text_liquid_level_intro_adjust_1',
				default:
					'Sensitivity presets (low, medium, high, or custom) control how readily the sensor switches between Clear and Detected.',
			},
			{
				key: 'text_liquid_level_intro_adjust_2',
				default:
					'The LED is off by default. You can set it to blink when liquid is Detected, or when the state is Clear.',
			},
		],
	},
	{
		key: 'controls',
		titleKey: 'title_liquid_level_intro_controls',
		title: 'Controls',
		tabTitleKey: 'title_liquid_level_intro_tab_controls',
		tabTitle: 'Button',
		subtitleKey: 'title_liquid_level_intro_controls_subtitle',
		subtitle: 'The one push button',
		paragraphs: [
			{
				key: 'text_liquid_level_intro_controls_1',
				default:
					'Press twice for Empty calibration, or three times for Full. The LED flashes red to confirm.',
			},
			{
				key: 'text_liquid_level_intro_controls_2',
				default:
					'Press 4 times to start Bluetooth pairing. The LED flashes red until pairing completes or times out.',
			},
			{
				key: 'text_liquid_level_intro_controls_3',
				default:
					'Press and hold for 5 seconds to clear calibration. Press and hold for 30 seconds to factory-reset the device.',
			},
			{
				key: 'text_liquid_level_intro_controls_4',
				default:
					'One short press also sends an immediate Bluetooth update with the latest sensor state.',
			},
		],
	},
];
